import type { CompanyDetailResponse } from '../api/screener-client.js';
import { basemapTileSrc } from '../basemap.js';
import type { GlobalCurrency } from '../currency.js';
import { formatMoney } from './screener-columns.js';
import { EMPTY_VALUE } from '../format.js';
import { MARKET_NAMES, bandToRadiusKm, catchmentForSite, type BoltOnClass } from '@hc/shared';
import {
  fetchCompanyCompetition,
  fetchBoltOn,
  fetchBoltOnMulti,
  fetchBoltOnFootprint,
  fetchCensus,
  type DossierCompetition,
  type CensusResponse,
  type BoltOnResponse,
} from '../api/client.js';

// Target Dossier — a one-click, print-ready EQUITY-RESEARCH-style report for one
// company. The Record panel already holds the company core (entity + metrics +
// scores + filed financials); the dossier additionally pulls the analytical
// context by REUSING the platform's own endpoints (so the numbers match the
// panels): competitive concentration (/companies/:id/competition), the catchment
// demographic profile (/census), and the bolt-on opportunity nearby (/bolt-on).
// Opens a self-contained, data-dense document in a new window for save-as-PDF.
// Screening altitude, not valuation — the footer says so.

interface DossierExtras {
  competition: DossierCompetition | null;
  catchment: CensusResponse | null;
  catchmentKm: number;
  /** The subsegment × urbanity catchment chosen for this site (mode + minutes
   *  + the urbanity tier it was derived from). null when none applies. */
  catchmentSpec: { mode: 'walk' | 'drive'; minutes: number; urbanity: string } | null;
  /** True when the subsegment is a B2B distributor (no residential catchment). */
  catchmentSuppressed: boolean;
  boltOn: {
    targets: number;
    competitors: number;
    targetRevEur: number;
    nearest: Array<{ name: string | null; revEur: number | null; distKm: number }>;
    /** One catchment per geocoded site — a single ring for a single-site
     *  operator, or one per site for a dispersed multi-site group (so the map
     *  shows the true footprint, not a misleading centroid). Each carries its
     *  own centre + isochrone ring ([lat,lng]; null → circle at radiusKm). */
    boundaries: Array<{
      lat: number;
      lng: number;
      polygon: Array<[number, number]> | null;
      radiusKm: number;
    }>;
    /** Target + competitor sites within the catchment(s), for the map dots. */
    points: Array<{ lat: number; lng: number; klass: BoltOnClass }>;
    /** True when stacked across >1 site (drives the legend wording). */
    multiSite: boolean;
    /** True when at least one boundary resolved a real drive-time isochrone. */
    isochrone: boolean;
  } | null;
  footprintSource: 'sites' | 'office' | null;
  siteCount: number;
}

/** Open the dossier in a new window (immediately, so the user gesture isn't
 *  lost to a popup blocker), fetch the analytical extras, then render. Returns
 *  false only if the window couldn't be opened (popup blocked). */
export async function openDossier(
  data: CompanyDetailResponse,
  ccy: GlobalCurrency,
): Promise<boolean> {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(loadingHtml(data.entity.legalName ?? 'target'));

  let extras: DossierExtras;
  try {
    extras = await fetchExtras(data);
  } catch {
    extras = {
      competition: null,
      catchment: null,
      catchmentKm: 0,
      catchmentSpec: null,
      catchmentSuppressed: false,
      boltOn: null,
      footprintSource: null,
      siteCount: 0,
    };
  }
  try {
    w.document.open();
    w.document.write(buildDossierHtml(data, extras, ccy));
    w.document.close();
  } catch {
    /* window closed by the user mid-generation — nothing to do. */
  }
  return true;
}

async function fetchExtras(data: CompanyDetailResponse): Promise<DossierExtras> {
  const id = data.entity.id;

  const [compRes, footprint] = await Promise.all([
    fetchCompanyCompetition(id).catch(() => ({ competition: null })),
    fetchBoltOnFootprint(id).catch(
      () =>
        ({
          lat: null,
          lng: null,
          subsegment: null,
          siteCount: 0,
          source: null,
          localDensityPerKm2: null,
        }) as const,
    ),
  ]);

  const sub = footprint.subsegment ?? data.metrics?.subsegment ?? null;
  // Catchment is subsegment- AND urbanity-specific (a pharmacy's walk-in radius
  // is nothing like a care home's drive-time draw). null = B2B distributor → no
  // residential catchment, so suppress the whole local section.
  const spec = catchmentForSite(sub, footprint.localDensityPerKm2 ?? null);
  const radiusKm = spec ? bandToRadiusKm(spec.minutes, spec.mode) : 0;
  let boltOn: DossierExtras['boltOn'] = null;
  let catchment: CensusResponse | null = null;

  if (spec && footprint.lat != null && footprint.lng != null && sub) {
    // Every geocoded site — a dispersed multi-site operator is mapped as ALL its
    // sites (each with its own catchment), not one misleading centroid. Fall
    // back to the footprint seed when site coords weren't returned.
    const seeds =
      footprint.sites && footprint.sites.length > 0
        ? footprint.sites.map((s) => ({ lat: s.lat, lng: s.lng }))
        : [{ lat: footprint.lat, lng: footprint.lng }];
    // ORS isochrones are rate-limited; cap the stacked cuts at the /bolt-on/multi
    // schema ceiling. Markers + census use the same set.
    const sites = seeds.slice(0, 30);
    const multiSite = sites.length > 1;

    // Resolve one catchment per site. Single site → the rich single endpoint; a
    // multi-site group → one own-subsegment catchment per site via
    // /bolt-on/multi. Both yield a BoltOnResponse per site (ring + candidates).
    let cuts: Array<{ lat: number; lng: number; res: BoltOnResponse | null }> = [];
    if (multiSite) {
      const multi = await fetchBoltOnMulti({
        members: sites.map((s) => ({ lat: s.lat, lng: s.lng, subsegment: sub })),
        band: spec.minutes,
        travel: spec.mode,
      }).catch(() => null);
      cuts = multi
        ? multi.members.map((m) => ({ lat: m.lat, lng: m.lng, res: m.catchment }))
        : sites.map((s) => ({ lat: s.lat, lng: s.lng, res: null }));
    } else {
      const bo = await fetchBoltOn({
        lat: sites[0]!.lat,
        lng: sites[0]!.lng,
        subsegment: sub,
        band: spec.minutes,
        travel: spec.mode,
        // True travel-time isochrone (same as the website's catchment); ORS-backed,
        // falls back to a straight-line radius server-side if ORS is unavailable.
        mode: 'isochrone',
      }).catch(() => null);
      cuts = [{ lat: sites[0]!.lat, lng: sites[0]!.lng, res: bo }];
    }

    // Per-site catchment boundaries to stack on the map (isochrone ring when ORS
    // resolved one, else a circle at the subsegment radius). Reused for BOTH the
    // map and the census cut so the demographics match the drawn footprint.
    const boundaries = cuts.map((c) => ({
      lat: c.lat,
      lng: c.lng,
      polygon:
        c.res?.boundary.polygon && c.res.boundary.polygon.length >= 3
          ? c.res.boundary.polygon
          : null,
      radiusKm,
    }));

    // Aggregate targets/competitors across all catchments, de-duplicated by
    // entity (overlapping site catchments would otherwise double-count). The
    // bolt-on opportunity is the OTHER reachable targets — exclude the company.
    const targetById = new Map<
      string,
      { name: string | null; revEur: number | null; distKm: number }
    >();
    const competitorIds = new Set<string>();
    const dots: Array<{ lat: number; lng: number; klass: BoltOnClass }> = [];
    for (const c of cuts) {
      if (!c.res) continue;
      for (const cand of c.res.candidates) {
        if (cand.entityId === id) continue;
        const key = cand.entityId ?? `${cand.lat},${cand.lng}`;
        const dist = cand.distanceKm ?? 0;
        const prev = targetById.get(key);
        if (!prev || dist < prev.distKm)
          targetById.set(key, { name: cand.name, revEur: cand.turnoverEur, distKm: dist });
      }
      for (const p of c.res.points) {
        if (p.entityId === id) continue;
        if (p.klass === 'competitor' && p.entityId) competitorIds.add(p.entityId);
        if (p.klass === 'target' || p.klass === 'competitor')
          dots.push({ lat: p.lat, lng: p.lng, klass: p.klass });
      }
    }
    const operators = [...targetById.values()].sort((a, b) => a.distKm - b.distKm);
    if (cuts.some((c) => c.res)) {
      boltOn = {
        targets: operators.length,
        competitors: competitorIds.size,
        targetRevEur: operators.reduce((s, c) => s + (c.revEur ?? 0), 0),
        nearest: operators.slice(0, 6),
        boundaries,
        points: dots,
        multiSite,
        isochrone: boundaries.some((b) => b.polygon != null),
      };
    }

    // Census over the SAME catchment(s): a MultiPolygon of all site rings
    // (/census dedupes points across subpolygons), so the demographics match the
    // drawn footprint. Single site → a plain Polygon.
    const rings = boundaries.map((b) =>
      b.polygon
        ? b.polygon.map(([la, lo]) => [lo, la] as number[])
        : circleRing(b.lat, b.lng, b.radiusKm),
    );
    const geometry =
      rings.length === 1
        ? { type: 'Polygon', coordinates: [rings[0]] }
        : { type: 'MultiPolygon', coordinates: rings.map((r) => [r]) };
    catchment = await fetchCensus({
      polygon: geometry,
      market: data.entity.countryCode,
    }).catch(() => null);
  }

  return {
    competition: compRes.competition,
    catchment,
    catchmentKm: radiusKm,
    catchmentSpec: spec,
    // B2B distributor (subsegment known but no residential catchment applies).
    catchmentSuppressed: sub != null && spec == null,
    boltOn,
    footprintSource: footprint.source ?? null,
    siteCount: footprint.siteCount,
  };
}

// --- geometry ---------------------------------------------------------------

/** Closed ring of [lng,lat] approximating a circle (equirectangular). */
function circleRing(lat: number, lng: number, radiusKm: number, points = 32): number[][] {
  const dLat = radiusKm / 110.574;
  const dLng = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180) || 1e-6);
  const ring: number[][] = [];
  for (let i = 0; i <= points; i++) {
    const t = (2 * Math.PI * i) / points;
    ring.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return ring;
}

// Web-Mercator world-pixel projection (256-tile, slippy-map convention).
const TILE = 256;
const worldX = (lng: number, z: number): number => ((lng + 180) / 360) * TILE * 2 ** z;
const worldY = (lat: number, z: number): number => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

/** Catchment map for print: the SAME look as the website's Bolt-On panel — the
 *  drive-time isochrone (orange outline + 5% fill) over CARTO Positron tiles —
 *  but rendered token-free and JS-free so it prints inside the PDF. The basemap
 *  is a static grid of CARTO @2x raster tiles; an SVG overlay draws EACH site's
 *  isochrone polygon (or a circle fallback) + a centre marker per site + target
 *  (green) / competitor (claret) dots, all projected via Web Mercator. A
 *  dispersed multi-site operator is shown as ALL its sites' stacked catchments
 *  (not one misleading midpoint). */
function boltOnMapHtml(
  boundaries: Array<{
    lat: number;
    lng: number;
    polygon: Array<[number, number]> | null;
    radiusKm: number;
  }>,
  points: Array<{ lat: number; lng: number; klass: BoltOnClass }>,
): string {
  const MW = 300;
  const MH = 224;
  // Each site's boundary ring as [lat,lng] — the isochrone when present, else a
  // circle around that site's centre.
  const rings: Array<Array<[number, number]>> = boundaries.map((b) =>
    b.polygon && b.polygon.length >= 3
      ? b.polygon
      : circleRing(b.lat, b.lng, b.radiusKm).map(([lng, lat]) => [lat, lng] as [number, number]),
  );
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity;
  for (const b of boundaries) {
    minLat = Math.min(minLat, b.lat);
    maxLat = Math.max(maxLat, b.lat);
    minLng = Math.min(minLng, b.lng);
    maxLng = Math.max(maxLng, b.lng);
  }
  for (const ring of rings)
    for (const [la, lo] of ring) {
      minLat = Math.min(minLat, la);
      maxLat = Math.max(maxLat, la);
      minLng = Math.min(minLng, lo);
      maxLng = Math.max(maxLng, lo);
    }
  // Largest zoom at which the combined bbox of all sites still fits (88% pad).
  let z = 16;
  for (; z >= 3; z--) {
    const w = Math.abs(worldX(maxLng, z) - worldX(minLng, z));
    const h = Math.abs(worldY(minLat, z) - worldY(maxLat, z));
    if (w <= MW * 0.88 && h <= MH * 0.88) break;
  }
  const n = 2 ** z;
  const originX = (worldX(minLng, z) + worldX(maxLng, z)) / 2 - MW / 2;
  const originY = (worldY(maxLat, z) + worldY(minLat, z)) / 2 - MH / 2;
  const subs = ['a', 'b', 'c'];
  let si = 0;
  let tiles = '';
  for (let xt = Math.floor(originX / TILE); xt <= Math.floor((originX + MW) / TILE); xt++) {
    for (let yt = Math.floor(originY / TILE); yt <= Math.floor((originY + MH) / TILE); yt++) {
      if (yt < 0 || yt >= n) continue;
      const xx = ((xt % n) + n) % n;
      const sub = subs[si++ % 3];
      tiles += `<img src="${basemapTileSrc(z, xx, yt, sub)}" width="${TILE}" height="${TILE}" style="position:absolute;left:${(xt * TILE - originX).toFixed(1)}px;top:${(yt * TILE - originY).toFixed(1)}px" alt=""/>`;
    }
  }
  const px = (lat: number, lng: number): [number, number] => [
    worldX(lng, z) - originX,
    worldY(lat, z) - originY,
  ];
  const ringPolys = rings
    .map((ring) => {
      const pts = ring
        .map(([la, lo]) => {
          const [x, y] = px(la, lo);
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
      return `<polygon points="${pts}" fill="#a8572a" fill-opacity="0.05" stroke="#a8572a" stroke-width="1.5"/>`;
    })
    .join('');
  const dots = (klass: BoltOnClass, fill: string): string =>
    points
      .filter((p) => p.klass === klass)
      .slice(0, 200)
      .map((p) => {
        const [x, y] = px(p.lat, p.lng);
        if (x < -4 || x > MW + 4 || y < -4 || y > MH + 4) return '';
        return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.1" fill="${fill}" stroke="#fff" stroke-width="0.6"/>`;
      })
      .join('');
  const siteMarkers = boundaries
    .map((b) => {
      const [cx, cy] = px(b.lat, b.lng);
      if (cx < -6 || cx > MW + 6 || cy < -6 || cy > MH + 6) return '';
      return (
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5" fill="none" stroke="#1a1410" stroke-width="1.6"/>` +
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="1.9" fill="#1a1410"/>`
      );
    })
    .join('');
  return (
    `<div style="position:relative;width:${MW}px;height:${MH}px;overflow:hidden;border:1px solid var(--line);border-radius:4px;">` +
    tiles +
    `<svg viewBox="0 0 ${MW} ${MH}" width="${MW}" height="${MH}" style="position:absolute;left:0;top:0" role="img" aria-label="Drive-time catchment with nearby targets and competitors">` +
    ringPolys +
    dots('competitor', '#c0143c') +
    dots('target', '#0a7d5a') +
    siteMarkers +
    `</svg>` +
    `<div style="position:absolute;right:3px;bottom:2px;font-size:7px;color:#555;background:rgba(255,255,255,.72);padding:0 3px;border-radius:2px">© OpenStreetMap © CARTO</div>` +
    `</div>`
  );
}

/** A print-ready static map of pins on CARTO Positron tiles — the same basemap
 *  as the catchment dossier, but generic (no catchment rings). Used by the
 *  Prescription build dossier for the platform's geographic footprint: the
 *  anchor pin is emphasised (dark ring + accent core), bolt-ons are accent
 *  dots. Token-free, JS-free, so it prints inside the PDF. */
export function staticPinMapHtml(
  pins: Array<{ lat: number; lng: number; anchor?: boolean }>,
  opts: { width?: number; height?: number } = {},
): string {
  const MW = opts.width ?? 300;
  const MH = opts.height ?? 224;
  if (pins.length === 0) return '';
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity;
  for (const p of pins) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  // Pad a tight/single-point cloud so we don't zoom in to street level.
  if (maxLat - minLat < 0.02) {
    minLat -= 0.05;
    maxLat += 0.05;
  }
  if (maxLng - minLng < 0.02) {
    minLng -= 0.05;
    maxLng += 0.05;
  }
  let z = 15;
  for (; z >= 3; z--) {
    const w = Math.abs(worldX(maxLng, z) - worldX(minLng, z));
    const h = Math.abs(worldY(minLat, z) - worldY(maxLat, z));
    if (w <= MW * 0.82 && h <= MH * 0.82) break;
  }
  const n = 2 ** z;
  const originX = (worldX(minLng, z) + worldX(maxLng, z)) / 2 - MW / 2;
  const originY = (worldY(maxLat, z) + worldY(minLat, z)) / 2 - MH / 2;
  const subs = ['a', 'b', 'c'];
  let si = 0;
  let tiles = '';
  for (let xt = Math.floor(originX / TILE); xt <= Math.floor((originX + MW) / TILE); xt++) {
    for (let yt = Math.floor(originY / TILE); yt <= Math.floor((originY + MH) / TILE); yt++) {
      if (yt < 0 || yt >= n) continue;
      const xx = ((xt % n) + n) % n;
      const sub = subs[si++ % 3];
      tiles += `<img src="${basemapTileSrc(z, xx, yt, sub)}" width="${TILE}" height="${TILE}" style="position:absolute;left:${(xt * TILE - originX).toFixed(1)}px;top:${(yt * TILE - originY).toFixed(1)}px" alt=""/>`;
    }
  }
  const px = (lat: number, lng: number): [number, number] => [
    worldX(lng, z) - originX,
    worldY(lat, z) - originY,
  ];
  const markers = pins
    .map((p) => {
      const [x, y] = px(p.lat, p.lng);
      if (x < -8 || x > MW + 8 || y < -8 || y > MH + 8) return '';
      if (p.anchor) {
        return (
          `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6.5" fill="#a8572a" stroke="#1a1410" stroke-width="1.6"/>` +
          `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.1" fill="#fff"/>`
        );
      }
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" fill="#a8572a" fill-opacity="0.9" stroke="#fff" stroke-width="0.8"/>`;
    })
    .join('');
  return (
    `<div style="position:relative;width:${MW}px;height:${MH}px;overflow:hidden;border:1px solid var(--line);border-radius:4px;">` +
    tiles +
    `<svg viewBox="0 0 ${MW} ${MH}" width="${MW}" height="${MH}" style="position:absolute;left:0;top:0" role="img" aria-label="Platform footprint map">` +
    markers +
    `</svg>` +
    `<div style="position:absolute;right:3px;bottom:2px;font-size:7px;color:#555;background:rgba(255,255,255,.72);padding:0 3px;border-radius:2px">© OpenStreetMap © CARTO</div>` +
    `</div>`
  );
}

// --- helpers ----------------------------------------------------------------

const esc = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const pct1 = (v: number | null | undefined): string =>
  v == null ? EMPTY_VALUE : `${(v * 100).toFixed(1)}%`;

function loadingHtml(name: string): string {
  return `<!doctype html><meta charset="utf-8"><title>${esc(name)} — Corryk Dossier</title><body style="margin:0;font:14px -apple-system,sans-serif;color:#555;display:flex;align-items:center;justify-content:center;height:100vh">Generating dossier for ${esc(name)}…</body>`;
}

// --- the report -------------------------------------------------------------

function buildDossierHtml(
  data: CompanyDetailResponse,
  x: DossierExtras,
  ccy: GlobalCurrency,
): string {
  const { entity, metrics, ukEnrichment, ukProperty, financials } = data;
  const cc = entity.countryCode;
  const money = (v: number | null | undefined) => formatMoney(v ?? null, cc, ccy);
  const dash = (v: unknown) => (v === null || v === undefined || v === '' ? EMPTY_VALUE : esc(v));
  const country = MARKET_NAMES[cc as keyof typeof MARKET_NAMES] ?? cc;
  const reported = metrics?.turnoverConfidenceP === 'P100';
  const today = new Date().toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  // Catchment is subsegment- AND urbanity-specific (a walk-in pharmacy vs a
  // drive-time care-home draw), expressed as a travel mode + time so the
  // isochrone self-scales the geographic footprint. Label states the basis.
  const spec = x.catchmentSpec;
  const catchLabel = spec
    ? `${spec.minutes}-min ${spec.mode} · ${spec.urbanity}`
    : x.catchmentKm > 0
      ? `≈${x.catchmentKm.toFixed(0)} km`
      : 'n/a';

  // derived signals (same logic the Record panel's Findings tab uses)
  const revSeries = financials
    .filter((f) => f.revenue != null && f.revenue > 0)
    .map((f) => ({ y: (f.periodEndDate ?? '').slice(0, 4), rev: f.revenue as number }))
    .sort((a, b) => a.y.localeCompare(b.y));
  let cagr: number | null = null;
  if (revSeries.length >= 2) {
    const first = revSeries[0]!,
      last = revSeries[revSeries.length - 1]!;
    const yrs = Math.max(1, Number(last.y) - Number(first.y));
    cagr = Math.pow(last.rev / first.rev, 1 / yrs) - 1;
  }
  const latest = financials[0] ?? null;
  const equityRatio =
    latest && latest.equity != null && latest.totalAssets != null && latest.totalAssets > 0
      ? latest.equity / latest.totalAssets
      : null;

  const stat = (label: string, value: string, sub?: string) =>
    `<div class="stat"><div class="stat-l">${esc(label)}</div><div class="stat-v">${value}</div>${
      sub ? `<div class="stat-s">${esc(sub)}</div>` : ''
    }</div>`;

  const snapshot = [
    stat(
      'Revenue',
      money(metrics?.turnoverScreener),
      reported
        ? 'reported'
        : `est${metrics?.turnoverConfidenceP ? ` · ${metrics.turnoverConfidenceP}` : ''}`,
    ),
    stat(
      'Origination Signal',
      metrics?.originationSignal != null ? `${metrics.originationSignal}` : EMPTY_VALUE,
      metrics?.originationConfidence ?? undefined,
    ),
    stat(
      'Seller Readiness Score',
      metrics?.sellerReadiness != null ? `${metrics.sellerReadiness}` : EMPTY_VALUE,
      metrics?.sellerReadinessConfidence ?? undefined,
    ),
    stat(
      'Employees',
      metrics?.employees != null ? metrics.employees.toLocaleString('en-GB') : EMPTY_VALUE,
    ),
    stat('Years active', metrics?.yearsActive != null ? String(metrics.yearsActive) : EMPTY_VALUE),
    stat('Net assets', money(metrics?.netAssets)),
    stat(
      'Sites',
      x.siteCount > 0 ? String(x.siteCount) : EMPTY_VALUE,
      x.footprintSource === 'office' ? 'office-based' : undefined,
    ),
    stat('Quality', dash(metrics?.qualityTier)),
  ].join('');

  // findings rows (label · value). noDot trims a trailing full stop off the
  // model-written Signal notes so they read as inline phrases, not sentences.
  const finding = (k: string, v: string) => `<tr><td>${esc(k)}</td><td>${v}</td></tr>`;
  const noDot = (s: string | null | undefined): string => esc((s ?? '').replace(/\s*\.\s*$/, ''));
  const findings = [
    finding(
      'Origination Signal',
      metrics?.originationSignal != null
        ? `${metrics.originationSignal}/100 — ${noDot(metrics.originationNote)}`
        : EMPTY_VALUE,
    ),
    finding(
      'Seller Readiness Score',
      metrics?.sellerReadiness != null
        ? `${metrics.sellerReadiness}/100 — ${noDot(metrics.sellerReadinessNote)}`
        : EMPTY_VALUE,
    ),
    finding(
      'Succession',
      metrics?.owner75Age != null
        ? `owner ${metrics.owner75Age}${metrics.owner75Age >= 65 ? ' · approaching/at retirement' : ''}`
        : 'no single 75%+ owner age on file',
    ),
    finding(
      'Revenue trajectory',
      cagr != null
        ? `${cagr >= 0 ? '+' : ''}${Math.round(cagr * 100)}%/yr over ${revSeries.length} filed yrs`
        : '<2 years on file',
    ),
    finding(
      'Balance sheet',
      equityRatio != null
        ? `${Math.round(equityRatio * 100)}% equity ${equityRatio < 0 ? '(negative — liabilities exceed assets)' : equityRatio < 0.2 ? '(geared)' : ''}`
        : metrics?.netAssets != null
          ? `net assets ${money(metrics.netAssets)}${metrics.netAssets < 0 ? ' (negative — liabilities exceed assets)' : ''}`
          : 'no balance sheet on file',
    ),
    finding(
      'Accounts / charges',
      `${metrics?.accountsOverdue ? 'accounts overdue' : 'filings current'}${metrics?.outstandingCharges ? ` · ${metrics.outstandingCharges} charge(s)` : ''}`,
    ),
    ...(ukEnrichment?.cqcOverallRating || ukEnrichment?.cqcLocations != null
      ? [
          finding(
            'Regulatory (CQC)',
            `${ukEnrichment.cqcOverallRating ? esc(ukEnrichment.cqcOverallRating) : 'rated'}${
              ukEnrichment.cqcLocations != null ? ` · ${ukEnrichment.cqcLocations} location(s)` : ''
            }${ukEnrichment.cqcCarehomeBeds ? ` · ${ukEnrichment.cqcCarehomeBeds} beds` : ''}`,
          ),
        ]
      : []),
  ].join('');

  // Property (GB). Who owns the building each site trades from — the fact that
  // decides whether the freehold comes with the business or whether the buyer is
  // acquiring an operating lease. GB-only by construction: HM Land Registry's
  // CCOD is the only bulk corporate property register across the 27 markets, so
  // the whole section is omitted elsewhere rather than printed empty.
  //
  // The evidence tier is stated in the copy, not implied by styling, because a
  // dossier is printed and read away from the tooltip that would otherwise carry
  // it. 'Related party' is an INFERENCE unless a shared controller confirmed it;
  // an unconfirmed holder is "not established", never "independent" — 41% of
  // unconfirmed edges are a PSC data gap rather than a demonstrated absence.
  const HOLDER_TXT: Record<string, string> = {
    operator: 'held by this company',
    related_party_psc: 'related party (shared controller, confirmed)',
    related_party_addr: 'related party (shared registered address — inferred)',
    related_party_name: 'related party (shared name — inferred)',
    third_party: 'relationship not established',
  };
  const prop = entity.countryCode === 'GB' && ukProperty?.sites.length ? ukProperty : null;
  const propertyFacts = prop
    ? [
        ['Titles found', String(prop.summary.titles)],
        ['Freehold / leasehold', `${prop.summary.freehold} / ${prop.summary.leasehold}`],
        ['Held by this company', String(prop.summary.operatorHeld)],
        ['Held by a related party', String(prop.summary.relatedParty)],
        ['Relationship not established', String(prop.summary.thirdParty)],
      ]
        .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`)
        .join('')
    : '';
  const propertyRows = prop
    ? prop.sites
        .slice(0, 40)
        .map(
          (t) =>
            `<tr><td>${esc(t.siteName ?? t.postcode ?? t.siteId)}</td><td>${esc(t.tenure ?? '—')}</td><td>${esc(
              t.holderName ?? '—',
            )}</td><td>${esc(HOLDER_TXT[t.holderClass ?? ''] ?? t.holderClass ?? '—')}</td><td>${esc(
              t.leaseTerm ?? '—',
            )}</td></tr>`,
        )
        .join('')
    : '';

  const ownership = [
    ['Controlling owner', dash(metrics?.owner75Name ?? metrics?.corporateParent)],
    ['Owner age', metrics?.owner75Age != null ? String(metrics.owner75Age) : EMPTY_VALUE],
    ['Group / parent', dash(metrics?.corporateParent)],
    ['Ownership type', dash(metrics?.ownershipType)],
  ]
    .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${v}</td></tr>`)
    .join('');

  // Financials. The headline turnover / net assets live on entity_normalised_
  // metrics (present for most companies); the multi-year period table is a
  // separate, sparse source (ingested iXBRL — most UK filers lodge abbreviated
  // accounts with no P&L). Show the reported headline ALWAYS, and the period
  // history only when it exists — never claim "no financials" while a reported
  // revenue is on file.
  const hasPeriods = financials.length > 0;
  const dormant = (metrics?.turnoverBasis ?? '').toLowerCase().startsWith('dormant');
  const classEstimate = metrics?.turnoverBasis === 'class_estimate';
  const basisTxt = classEstimate
    ? 'Class estimate (per-clinician, scope only)'
    : (metrics?.turnoverBasis ?? (reported ? 'Reported' : 'Estimated'));
  // Class estimate is a scope-gate figure, never a precise turnover — prefix ~.
  const revHeadline =
    metrics?.turnoverScreener != null
      ? `${classEstimate ? '~' : ''}${money(metrics.turnoverScreener)}`
      : EMPTY_VALUE;
  const financialsHtml = hasPeriods
    ? `<table><thead><tr><th>Period</th><th class="num">Revenue</th><th class="num">Assets</th><th class="num">Equity</th></tr></thead><tbody>${financials
        .map(
          (f) =>
            `<tr><td>${esc((f.periodEndDate ?? '').slice(0, 10))}</td><td class="num">${money(f.revenue)}</td><td class="num">${money(f.totalAssets)}</td><td class="num">${money(f.equity)}</td></tr>`,
        )
        .join('')}</tbody></table>${revChart(revSeries, money)}`
    : `<table class="facts"><tbody>` +
      `<tr><td>Revenue (${esc(basisTxt)})</td><td class="num">${revHeadline}</td></tr>` +
      `<tr><td>Net assets</td><td class="num">${money(metrics?.netAssets)}</td></tr>` +
      `<tr><td>Employees</td><td class="num">${metrics?.employees != null ? metrics.employees.toLocaleString('en-GB') : EMPTY_VALUE}</td></tr>` +
      `</tbody></table>` +
      `<div class="note">${
        dormant
          ? 'Company reports as <b>dormant</b> (nil turnover).'
          : `No itemised multi-year filing history on record — the figures above are the ${reported ? 'reported headline' : 'modelled estimate'}${metrics?.turnoverConfidenceP && !reported ? ` (${esc(metrics.turnoverConfidenceP)})` : ''}. UK filers commonly lodge abbreviated accounts without a profit &amp; loss statement.`
      }</div>`;

  // --- catchment (census) ---
  const cat = x.catchment;
  let elderlyPct: number | null = null;
  if (cat && cat.people > 0) {
    let e = 0;
    for (const [k, v] of Object.entries(cat.age_structure)) {
      const lo = Number(k.replace(/^age_/, '').split('_')[0]);
      if (Number.isFinite(lo) && lo >= 65) e += Number(v) || 0;
    }
    elderlyPct = e / cat.people;
  }
  const catchmentFacts = cat
    ? `<tr><td>Catchment population</td><td class="num">${cat.people.toLocaleString('en-GB')}</td></tr>` +
      `<tr><td>Households</td><td class="num">${cat.households != null ? cat.households.toLocaleString('en-GB') : EMPTY_VALUE}</td></tr>` +
      `<tr><td>Income percentile <span class="muted">(vs national, 50 = median)</span></td><td class="num">${cat.income_percentile != null ? Math.round(cat.income_percentile) : EMPTY_VALUE}</td></tr>` +
      `<tr><td>Aged 65+</td><td class="num">${pct1(elderlyPct)}</td></tr>` +
      `<tr><td>Demand index <span class="muted">(vs 100)</span></td><td class="num">${cat.demand_index != null ? String(cat.demand_index) : EMPTY_VALUE}</td></tr>`
    : '';

  // --- competitive landscape ---
  const comp = x.competition;
  const compTop = comp
    ? comp.top
        .map(
          (g) =>
            `<tr${g.isThis ? ' class="me"' : ''}><td>${esc(g.label)}${g.isThis ? ' ◂ this' : ''}</td><td class="num">${money(g.revEur)}</td><td class="num">${pct1(g.share)}</td></tr>`,
        )
        .join('')
    : '';

  // --- bolt-on opportunity ---
  const bo = x.boltOn;
  const boNearest = bo
    ? bo.nearest
        .map(
          (t) =>
            `<tr><td>${esc(t.name ?? 'Target')}</td><td class="num">${money(t.revEur)}</td><td class="num">${t.distKm.toFixed(1)}km</td></tr>`,
        )
        .join('')
    : '';

  // Self-contained catchment scatter map (targets + competitor sites around the
  // company), shown alongside the local bolt-on / census numbers.
  const mapHtml = bo ? boltOnMapHtml(bo.boundaries, bo.points) : '';

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
<title>${esc(entity.legalName ?? 'Target')} — Corryk Dossier</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap');
  /* Corryk palette — the product's own tokens (packages/ui/tokens.css):
     FT-cream canvas, burnt-orange accent, warm near-black ink, warm hairline,
     green/claret for direction. Editorial serif masthead + Inter for data. */
  :root{
    --ink:#1a1410;--mid:#424a53;--muted:#6e7681;--line:#d6c9b8;
    --accent:#a8572a;--positive:#0a7d5a;--negative:#c0143c;
    --canvas:#fff1e5;--paper:#ffffff;--elevated:#f7ecdc;
    --font-ui:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    --font-ed:'Source Serif 4','Source Serif Pro',Georgia,'Times New Roman',serif;
  }
  *{box-sizing:border-box;}
  html,body{margin:0;background:var(--canvas);color:var(--ink);font:11px/1.38 var(--font-ui);}
  .sheet{max-width:820px;margin:12px auto;background:var(--paper);padding:18px 22px 12px;box-shadow:0 1px 6px rgba(60,40,20,.16);}
  .brandbar{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid var(--accent);padding-bottom:4px;margin-bottom:8px;}
  .brand{font-family:var(--font-ed);font-weight:600;letter-spacing:.01em;color:var(--accent);font-size:14px;}
  .brand small{font-family:var(--font-ui);color:var(--muted);font-weight:500;letter-spacing:0;font-size:10.5px;}
  .meta{font-size:10px;color:var(--muted);}
  h1{font-family:var(--font-ed);font-size:19px;font-weight:600;letter-spacing:-.01em;margin:4px 0 1px;line-height:1.1;}
  .sub{font-size:11px;color:var(--muted);margin-bottom:9px;}
  .grid{display:grid;grid-template-columns:repeat(8,1fr);gap:5px;margin-bottom:6px;}
  .stat{border:1px solid var(--line);border-radius:4px;padding:4px 6px;background:var(--paper);display:flex;flex-direction:column;}
  .stat-l{font-size:8px;color:var(--muted);text-transform:uppercase;letter-spacing:.02em;line-height:1.12;min-height:18px;}
  .stat-v{font-size:13px;font-weight:600;font-variant-numeric:tabular-nums;line-height:1.15;}
  .stat-s{font-size:8px;color:var(--muted);}
  h2{font-family:var(--font-ui);font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--accent);border-bottom:1px solid var(--line);padding-bottom:2px;margin:11px 0 5px;}
  .cols{display:grid;grid-template-columns:1fr 1fr;gap:18px;align-items:start;}
  .maprow{grid-template-columns:300px 1fr;}
  .maplegend{display:flex;gap:12px;flex-wrap:wrap;margin-top:4px;font-size:9.5px;color:var(--mid);}
  .dotc{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:4px;vertical-align:middle;}
  table{width:100%;border-collapse:collapse;font-size:11px;}
  td,th{padding:2.2px 5px;border-bottom:1px solid var(--line);text-align:left;vertical-align:top;}
  th{font-size:9px;text-transform:uppercase;color:var(--muted);letter-spacing:.03em;}
  td:first-child{color:var(--muted);}
  .findings td:nth-child(2),.facts td:nth-child(2){color:var(--ink);}
  .num{text-align:right;font-variant-numeric:tabular-nums;color:var(--ink);}
  .pos{color:var(--positive);}.neg{color:var(--negative);}
  tr.me td{background:var(--elevated);font-weight:600;}
  .muted{color:var(--muted);}
  .note{font-size:10px;color:var(--muted);line-height:1.4;margin-top:5px;}
  .chart{display:flex;align-items:flex-end;gap:8px;height:48px;margin:4px 0 2px;}
  .bar{flex:1;background:var(--accent);opacity:.85;border-radius:2px 2px 0 0;min-height:2px;position:relative;}
  .bar span{position:absolute;top:-12px;left:0;right:0;text-align:center;font-size:8.5px;color:var(--ink);}
  .bar small{position:absolute;bottom:-13px;left:0;right:0;text-align:center;font-size:8.5px;color:var(--muted);}
  .printbar{position:fixed;top:14px;right:14px;}
  .printbar button{font:600 13px var(--font-ui);background:var(--accent);color:#fff;border:0;border-radius:6px;padding:8px 14px;cursor:pointer;box-shadow:0 1px 4px rgba(60,40,20,.25);}
  .foot{margin-top:10px;padding-top:6px;border-top:1px solid var(--line);font-size:9px;color:var(--muted);line-height:1.45;}
  @page{size:A4;margin:9mm;}
  @media print{html,body{background:#fff;}.sheet{box-shadow:none;margin:0;max-width:none;padding:0;}.printbar{display:none;}h2,table,.grid,.cols,.chart,svg{page-break-inside:avoid;}}
</style></head>
<body>
  <div class="printbar"><button onclick="window.print()">Save as PDF / Print</button></div>
  <div class="sheet">
    <div class="brandbar"><div class="brand">Corryk <small>· Target Dossier</small></div><div class="meta">Generated ${esc(today)}</div></div>
    <h1>${esc(entity.legalName ?? 'Unnamed target')}</h1>
    <div class="sub">${esc(metrics?.subsegment ?? 'Healthcare services')} · ${entity.region ? esc(entity.region) + ' · ' : ''}${esc(country)}${entity.registryId ? ' · ' + esc(entity.registryId) : ''}${entity.status ? ' · ' + esc(entity.status) : ''}</div>

    <div class="grid">${snapshot}</div>

    <h2>Investment signals</h2>
    <table class="findings"><tbody>${findings}</tbody></table>

    <div class="cols">
      <div>
        <h2>Financials</h2>
        ${financialsHtml}
      </div>
      <div>
        <h2>Ownership &amp; control</h2>
        <table class="facts"><tbody>${ownership}</tbody></table>
      </div>
    </div>

    ${
      prop
        ? `<h2>Property &amp; tenure</h2>
    <div class="sub" style="margin:0 0 5px">HM Land Registry (CCOD), title matched to the site address. Only “held by this company” is an exact-identifier match; related-party rungs are inferences unless a shared controller confirmed them.</div>
    <div class="cols">
      <div><table class="facts"><tbody>${propertyFacts}</tbody></table></div>
      <div><table><thead><tr><th>Site</th><th>Tenure</th><th>Title holder</th><th>Relationship</th><th>Lease term</th></tr></thead><tbody>${propertyRows}</tbody></table></div>
    </div>`
        : ''
    }

    <h2>Competitive landscape${comp ? ` — ${comp.scope}` : ' — n/a'}</h2>
    ${
      comp
        ? `<div class="sub" style="margin:0 0 5px">${comp.scope === 'regional' && comp.region ? `<b>${esc(comp.region)}</b> · ` : ''}${esc(comp.subsegment)} · ${esc(MARKET_NAMES[comp.country as keyof typeof MARKET_NAMES] ?? comp.country)} — ${comp.groups.toLocaleString('en-GB')} groups · CR5 ${pct1(comp.cr5)} · HHI ${comp.hhi}${comp.thisRank ? ` · this group #${comp.thisRank} (${pct1(comp.thisShare)})` : ''}${comp.scope === 'national' ? ' <span class="muted">· national (no region on file)</span>' : ''}</div>
    <table><thead><tr><th>Largest groups${comp.scope === 'regional' && comp.region ? ` in ${esc(comp.region)}` : ''}</th><th class="num">Revenue</th><th class="num">Share</th></tr></thead><tbody>${compTop}</tbody></table>`
        : '<div class="muted">No market context available.</div>'
    }

    <h2>Local catchment &amp; bolt-ons${x.catchmentSuppressed || !(bo || cat) ? ' — n/a' : ` — ${catchLabel}`}</h2>
    ${
      x.catchmentSuppressed
        ? '<div class="muted">Distribution / wholesale business — a residential catchment isn’t meaningful here, so it isn’t shown.</div>'
        : bo || cat
          ? `<div class="cols maprow">
      <div>
        ${bo ? mapHtml : '<div class="muted">No geocoded site to map.</div>'}
        ${bo ? `<div class="maplegend"><span><i class="dotc" style="background:#1a1410"></i>${bo.multiSite ? `Sites (${bo.boundaries.length})` : 'Company'}</span><span><i class="dotc" style="background:#0a7d5a"></i>Targets (${bo.targets})</span><span><i class="dotc" style="background:#c0143c"></i>Competitors (${bo.competitors})</span></div>` : ''}
      </div>
      <div>
        ${cat ? `<table class="facts"><tbody>${catchmentFacts}</tbody></table>` : '<div class="muted">No census profile for this catchment.</div>'}
        ${
          bo
            ? `<div class="note">${bo.targets} reachable independent target(s) · ${money(bo.targetRevEur)} combined est. revenue · <b>${bo.competitors}</b> competitor-owned site(s) ${bo.multiSite ? `across the operator’s ${bo.boundaries.length} site catchments` : 'inside this catchment'}.</div>
        <table style="margin-top:5px"><thead><tr><th>Nearest targets</th><th class="num">Rev</th><th class="num">Dist</th></tr></thead><tbody>${boNearest || '<tr><td colspan="3" class="muted">No independent targets in range.</td></tr>'}</tbody></table>`
            : ''
        }
      </div>
    </div>`
          : '<div class="muted">No geocoded site to profile a catchment or scan for bolt-ons.</div>'
    }

    <div class="foot">Revenue and scores are <b>modelled indicators for screening, not audited figures or valuation advice</b>. Estimated revenue carries a confidence rating (P = calibrated probability the true figure is within &plusmn;50%). Origination Signal ranks acquisition-attractiveness; the Seller Readiness Score reads owner gettability. Catchment is sized to the sub-segment and local urbanity${spec ? ` — a ${spec.minutes}-min ${spec.mode}${x.boltOn?.isochrone ? ' isochrone' : ` (≈${x.catchmentKm.toFixed(0)} km radius)`}` : ''} around the company’s geocoded site(s); bolt-on figures and demographics are local to it, whereas competitive concentration is ${comp?.scope ?? 'regional'} within the sub-segment. Figures in ${esc(ccy)} where convertible. © Corryk.</div>
  </div>
</body></html>`;
}

/** Inline SVG-free bar chart of filed revenue (chronological), for print. */
function revChart(series: Array<{ y: string; rev: number }>, money: (v: number) => string): string {
  if (series.length < 2) return '';
  const max = Math.max(...series.map((s) => s.rev));
  const bars = series
    .map((s) => {
      const h = max > 0 ? Math.round((100 * s.rev) / max) : 0;
      return `<div class="bar" style="height:${h}%"><span>${esc(money(s.rev))}</span><small>${esc(s.y)}</small></div>`;
    })
    .join('');
  return `<div class="chart">${bars}</div><div style="height:14px"></div>`;
}
